import { DesignData } from 'editor/src/store/design/types';
import { addUploadedFontsAction } from 'editor/src/store/fonts/slice';
import { UploadedFontData } from 'editor/src/store/fonts/types';
import type { AsyncThunk } from 'editor/src/store/hooks';

import { getEcomProxyEndpoint, getStoreConfig } from './config';

import type { FontWeight } from '@gelatoas/design-editor-fonts';

const FONTS_API_PATH = '/fonts';

interface ClientFontResponse {
  id: string;
  clientId: string;
  name: string;
  family: string;
  style: string | null;
  originalFilename: string;
  isProtected: boolean;
  fileUrl?: string;
  previewUrl?: string;
}

interface ClientFontListResponse {
  fonts: ClientFontResponse[];
}

function mapToUploadedFontData(font: ClientFontResponse): UploadedFontData {
  return {
    fontFile: `custom-${font.id}`,
    name: font.name,
    family: font.family,
    weight: (font.style || 'Regular') as FontWeight,
    style: 'Normal',
    fileUrl: font.fileUrl,
    filename: font.originalFilename,
    isProtected: font.isProtected,
    previewUrl: font.previewUrl,
  };
}

function extractCustomFontFamilies(designData: DesignData): string[] {
  const families = new Set<string>();
  designData.spreads.forEach((spread) => {
    spread.pages.forEach((page) => {
      page.groups.media?.forEach((element) => {
        if (element.type === 'text' && element.extra?.fontFamily?.startsWith('custom-')) {
          families.add(element.extra.fontFamily);
        }
      });
    });
  });
  return Array.from(families);
}

/**
 * After design data loads, check for custom font families.
 * If found, fetch font metadata from the API so non-protected fonts
 * can be loaded client-side for instant text rendering.
 */
const loadCustomFontsFromApi =
  (designData: DesignData): AsyncThunk =>
  async (dispatch, getState) => {
    const customFontFamilies = extractCustomFontFamilies(designData);
    if (customFontFamilies.length === 0) {
      return;
    }

    // Don't re-fetch if fonts are already in state
    const { uploadedFonts } = getState().fonts;
    const missingFonts = customFontFamilies.filter((family) => !uploadedFonts.some((f) => f.fontFile === family));
    if (missingFonts.length === 0) {
      return;
    }

    // The ecommerce fonts endpoint requires clientId as a query param (returns
    // 400 "clientId should not be blank" without it). It is NOT resolved
    // server-side on every storefront, so pass it explicitly from the runtime
    // config. Without it the metadata fetch fails, uploadedFonts has no fileUrl,
    // the custom font never loads, calcTextWidth() returns 0, and dynamic-width
    // (flexibleWidth) reflow text can't size — it renders tiny.
    const { clientId } = getStoreConfig();
    const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    const url = `${getEcomProxyEndpoint()}${FONTS_API_PATH}${query}`;

    // eslint-disable-next-line no-console
    console.log('[PP:CustomFont] Fetching font metadata', {
      customFontFamilies: missingFonts,
      url,
    });

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Font list fetch failed: ${response.status}`);
      }

      const data: ClientFontListResponse = await response.json();
      const fonts = (data.fonts || []).map(mapToUploadedFontData);

      if (fonts.length > 0) {
        dispatch(addUploadedFontsAction(fonts));
        // eslint-disable-next-line no-console
        console.log('[PP:CustomFont] Font metadata loaded', {
          totalFonts: fonts.length,
          nonProtected: fonts.filter((f) => !f.isProtected && f.fileUrl).length,
          protected: fonts.filter((f) => f.isProtected).length,
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PP:CustomFont] Failed to fetch font metadata, falling back to render API', err);
    }
  };

export default loadCustomFontsFromApi;
